import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";

import { useAuth } from "../../../app/providers/AuthProvider";
import { useOrderEvents } from "../../../data/event-reconciliation/useOrderEvents";
import { orderKeys, type OrderFilters } from "../../../data/query-keys/orders";
import type {
  OrderDetails,
  OrderSnapshotItem,
  OrderSummary,
} from "../../../shared/api/types";
import { getSetting, setSetting } from "../../../storage/indexed-db/database";
import { useQueueWorker } from "../../../sync/reconnect/useQueueWorker";
import { useOrders } from "../hooks/useOrders";
import { useOrdersUiStore } from "../model/ordersStore";

type OrdersQuery = ReturnType<typeof useOrders>;

interface OrdersRuntimeValue {
  filtersReady: boolean;
  orders: OrderSummary[];
  ordersQuery: OrdersQuery;
  snapshotItems: OrderSnapshotItem[];
}

const OrdersRuntimeContext = createContext<OrdersRuntimeValue | null>(null);

export function mergeSnapshotDetails(
  current: OrderDetails | undefined,
  incoming: OrderDetails,
): OrderDetails {
  if (!current) return incoming;
  if (current.syncState) {
    return {
      ...incoming,
      status: current.status,
      entityVersion: current.entityVersion,
      syncState: current.syncState,
    };
  }
  return incoming.entityVersion > current.entityVersion ? incoming : current;
}

export function OrdersRuntimeProvider({ children }: PropsWithChildren) {
  const auth = useAuth();
  const client = useQueryClient();
  const filters = useOrdersUiStore((state) => state.filters);
  const setFilters = useOrdersUiStore((state) => state.setFilters);
  const [filtersReady, setFiltersReady] = useState(false);

  useEffect(() => {
    let active = true;
    void getSetting<OrderFilters>("orders-filters").then((saved) => {
      if (!active) return;
      if (saved) setFilters(saved);
      setFiltersReady(true);
    });
    return () => {
      active = false;
    };
  }, [setFilters]);

  useEffect(() => {
    if (filtersReady) void setSetting("orders-filters", filters);
  }, [filters, filtersReady]);

  const ordersQuery = useOrders(
    filters,
    filtersReady && auth.state === "authenticated",
  );
  const snapshotItems = useMemo(
    () => ordersQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [ordersQuery.data],
  );
  const orders = useMemo(
    () => snapshotItems.map((item) => item.summary),
    [snapshotItems],
  );
  const visibleOrderIds = useMemo(
    () => orders.map((order) => order.id),
    [orders],
  );
  const snapshotEventCursor = useMemo(() => {
    const cursors = ordersQuery.data?.pages.map((page) => page.eventCursor) ?? [];
    return cursors.length > 0 ? Math.min(...cursors) : null;
  }, [ordersQuery.data]);

  useEffect(() => {
    for (const item of snapshotItems) {
      client.setQueryData<OrderDetails>(
        orderKeys.detail(item.details.id),
        (current) => mergeSnapshotDetails(current, item.details),
      );
    }
  }, [client, snapshotItems]);

  useQueueWorker(client, auth.state === "authenticated");
  const refetchOrders = ordersQuery.refetch;
  const recoverSnapshot = useCallback(() => {
    void refetchOrders();
  }, [refetchOrders]);
  useOrderEvents(
    client,
    auth.state === "authenticated" && snapshotEventCursor !== null,
    visibleOrderIds,
    snapshotEventCursor ?? 0,
    recoverSnapshot,
  );

  const value = useMemo<OrdersRuntimeValue>(
    () => ({ filtersReady, orders, ordersQuery, snapshotItems }),
    [filtersReady, orders, ordersQuery, snapshotItems],
  );

  return (
    <OrdersRuntimeContext.Provider value={value}>
      {children}
    </OrdersRuntimeContext.Provider>
  );
}

export function useOrdersRuntime(): OrdersRuntimeValue {
  const value = useContext(OrdersRuntimeContext);
  if (!value) {
    throw new Error("useOrdersRuntime must be used inside OrdersRuntimeProvider");
  }
  return value;
}
